// components/UpgradeCheckoutForm.js
//
// The part every upgrade checkout shares: the price in the buyer's currency,
// their billing details, and the Pay button. Used by the three purchase
// modals on the upgrade page (invitation tier, photo uploads, album storage),
// which each add what is particular to them above it.
//
// The country drives both the currency and the tax, so it is asked for and
// prefilled rather than inferred silently: India pays in rupees with GST on
// top (CGST + SGST in the seller's state, IGST elsewhere); everywhere else
// pays a local price with 18% IGST inside it. See shared/upgradePricing.js.

import React, { useEffect, useMemo, useState } from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import supabase from '../services/supabase';
import { SELLER_STATE } from '../services/upgradeCheckout';
import { detectCountry } from '../services/visitorCountry';
import { formatMoney } from '../shared/upgradePricing';
import { getIndianStates } from '../services/eventUpgradeInvoiceService';

/** Offered first; any other detected country is added to the list. */
const COUNTRIES = [
  'IN', 'US', 'GB', 'CA', 'AU', 'AE', 'SG', 'NZ', 'IE', 'DE', 'FR', 'NL', 'IT', 'ES',
  'PT', 'BE', 'AT', 'CH', 'SE', 'NO', 'DK', 'FI', 'SA', 'QA', 'KW', 'OM', 'BH', 'MY',
  'HK', 'JP', 'ZA', 'NP', 'LK', 'BD', 'MU',
];

const regionName = (() => {
  try {
    const names = new Intl.DisplayNames(['en'], { type: 'region' });
    return (code) => names.of(code) || code;
  } catch {
    return (code) => code;
  }
})();

export default function UpgradeCheckoutForm({
  user,
  /** (country, state) => Quote | null - see shared/upgradePricing.js */
  quoteFor,
  busy = false,
  error = '',
  onPay,
  onCancel,
  /** What is being bought (duration picker, album size...), shown above the price. */
  children,
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('IN');
  const [state, setState] = useState('');
  const [formError, setFormError] = useState('');
  const indianStates = getIndianStates();

  // Where they are, as a default they can change.
  useEffect(() => {
    let cancelled = false;
    detectCountry().then((c) => { if (!cancelled && c) setCountry(c); });
    return () => { cancelled = true; };
  }, []);

  // Their saved details, so the form is usually already filled in.
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    supabase
      .from('users')
      .select('first_name, last_name, phone_number, email, state')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (cancelled || !data) return;
        setName([data.first_name, data.last_name].filter(Boolean).join(' '));
        if (data.email) setEmail(data.email);
        if (data.phone_number) {
          let p = data.phone_number.replace(/[\s\-()]/g, '');
          if (p.startsWith('+91')) p = p.slice(3);
          else if (p.startsWith('91') && p.length > 10) p = p.slice(2);
          setPhone(p);
        }
        if (data.state) setState(data.state);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  const india = country === 'IN';
  const quote = useMemo(() => {
    try {
      return quoteFor(country, india ? state : '');
    } catch {
      return null;
    }
  }, [quoteFor, country, state, india]);

  const countries = COUNTRIES.includes(country) ? COUNTRIES : [country, ...COUNTRIES];
  const money = (n) => (quote ? formatMoney(n, quote.currency) : '');

  const submit = () => {
    if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFormError('Please enter your name and a valid email for the invoice.');
      return;
    }
    const digits = phone.replace(/[^\d]/g, '');
    if (india && !/^[6-9]\d{9}$/.test(digits)) {
      setFormError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!india && digits && (digits.length < 6 || digits.length > 15)) {
      setFormError('Please enter a valid phone number, or leave it empty.');
      return;
    }
    if (!quote) {
      setFormError('This is not available in your country yet.');
      return;
    }
    setFormError('');
    onPay({
      name: name.trim(),
      email: email.trim(),
      phone: india ? `+91${digits}` : (phone.trim() || ''),
      state: india ? state : '',
      country,
    });
  };

  const shownError = formError || error;

  return (
    <>
      <div className="modal-content">
        {children}

        {quote && (
          <div className="pricing-summary">
            {quote.taxInclusive ? (
              <>
                <div className="price-row">
                  <span>Price</span>
                  <span>{money(quote.base)}</span>
                </div>
                <div className="price-row">
                  <span>IGST (18%)</span>
                  <span>{money(quote.igst)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="price-row">
                  <span>Base price</span>
                  <span>{money(quote.base)}</span>
                </div>
                {quote.gstType === 'intrastate' ? (
                  <>
                    <div className="price-row"><span>CGST (9%)</span><span>{money(quote.cgst)}</span></div>
                    <div className="price-row"><span>SGST (9%)</span><span>{money(quote.sgst)}</span></div>
                  </>
                ) : (
                  <div className="price-row"><span>IGST (18%)</span><span>{money(quote.igst)}</span></div>
                )}
              </>
            )}
            <div className="price-row total">
              <span>Total</span>
              <span>{money(quote.total)}</span>
            </div>
            {!india && (
              <small className="help-text">
                Charged in {quote.currency}; your bank may show the converted amount.
              </small>
            )}
          </div>
        )}

        <div className="customer-form">
          <h3>Billing details</h3>

          <div className="form-group">
            <label htmlFor="upg-name">Full name *</label>
            <input id="upg-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Name for the invoice" autoComplete="name" />
          </div>

          <div className="form-group">
            <label htmlFor="upg-email">Email *</label>
            <input id="upg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Where the invoice goes" autoComplete="email" />
          </div>

          <div className="form-group">
            <label htmlFor="upg-country">Country *</label>
            <select id="upg-country" value={country} onChange={(e) => setCountry(e.target.value)}>
              {countries.map((c) => <option key={c} value={c}>{regionName(c)}</option>)}
            </select>
            <small className="help-text">Sets your currency and the tax on your invoice.</small>
          </div>

          <div className="form-group">
            <label htmlFor="upg-phone">{india ? 'Phone *' : 'Phone'}</label>
            <input id="upg-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder={india ? '10-digit mobile number' : 'With country code, e.g. +1 415 555 0100'}
              maxLength={india ? 10 : 20} autoComplete="tel" />
          </div>

          {india && (
            <div className="form-group">
              <label htmlFor="upg-state">State (for GST)</label>
              <select id="upg-state" value={state} onChange={(e) => setState(e.target.value)}>
                <option value="">Select state</option>
                {indianStates.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <small className="help-text">
                {state === SELLER_STATE ? 'CGST + SGST apply in Chhattisgarh.' : 'IGST applies outside Chhattisgarh.'}
              </small>
            </div>
          )}
        </div>

        {shownError && (
          <div className="error-message">
            <FiAlertCircle />
            <span>{shownError}</span>
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button type="button" className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-pay" onClick={submit} disabled={busy || !quote}>
          {quote ? `Pay ${money(quote.total)}` : 'Not available'}
        </button>
      </div>
    </>
  );
}
